<template>
  <div
    ref="containerEl"
    v-hotkey="shortcuts"
    v-click-outside="unselectTask"
    class="recurrences-task-list"
    :class="{ empty: recurrentTasks && recurrentTasks.length === 0 }"
  >
    <AddRecurringTaskButton class="add-recurring-task" @focus="unselectTask" />
    <BaseEmptyTasklist
      v-if="recurrentTasks && recurrentTasks.length === 0"
      class="empty-recurrences"
      text="Overview all your recurring tasks"
    />
    <Suspense v-else>
      <div ref="taskListEl" class="items">
        <div
          v-for="{ frequency, bucket } in recurrentTasks"
          :key="`recurrences-${getId(frequency)}`"
          class="section"
        >
          <span class="title">{{ getTitle(frequency) }}</span>
          <TaskList
            is-recurrent
            :task-ids="bucket"
            :selected-task-id="selectedTaskId"
            :is-editing="isEditing"
            @quit-editing="quitEditing"
            @set-selected-task-el="(el) => setSelectedTaskEl(el)"
            @task-click="(id) => selectTaskById(id)"
          />
        </div>
        <div class="unselect-area" @click="unselectTask" />
      </div>
      <template #fallback>
        <Loader>
          <BaseSkeletonList :size="2" />
        </Loader>
      </template>
    </Suspense>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";

import { FrequencyBucket, Rule } from "@ctrl/controller-rpc";

import AddRecurringTaskButton from "@/components/AddRecurringTaskButton.vue";
import BaseEmptyTasklist from "@/components/base/BaseEmptyTasklist.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import { getRecurrenceText } from "@/components/recurrence";
import { useTaskList } from "@/components/task/useTaskList";
import TaskList from "@/components/tasks/TaskList.vue";
import { useReleaser } from "@/ref";
import { openExternalUrl } from "@/utils/open-external-url";

export default defineComponent({
  name: "Recurrences",
  components: {
    AddRecurringTaskButton,
    Loader,
    BaseEmptyTasklist,
    BaseSkeletonList,
    TaskList,
  },
  props: {
    length: { type: Number, required: true },
    // Accepted for parity with the other Tasks tabs (PersonalTasks binds it
    // on whichever tab is displayed): recurrences are never paginated, so
    // there is nothing to bypass.
    showAll: { type: Boolean, default: false },
  },
  emits: {
    "update:length": (_nbr: number) => true,
  },
  async setup(_, { emit }) {
    const console = inject.consoleModal();
    const controller = inject.controller();
    const contextMenu = inject.contextMenu();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const containerEl = ref<HTMLDivElement>();
    const openUrl = (url: string) => openExternalUrl(url, controller);
    const getTaskIds = (recTasks: Array<FrequencyBucket>) =>
      recTasks.flatMap(
        (recurrentTaskByFrequence) => recurrentTaskByFrequence.bucket
      );
    const releaser = useReleaser();
    const recurrentTasksPromise = releaser.add(controller.taskRecurrent.all());
    recurrentTasksPromise.then((recTasks) => {
      if (recTasks.value) {
        const ids = getTaskIds(recTasks.value);
        emit("update:length", ids.length);
      }
    });
    const recurrentTasks = await recurrentTasksPromise;
    const taskIds = computed(() =>
      recurrentTasks.value ? getTaskIds(recurrentTasks.value) : null
    );
    releaser.defer(
      watch(
        () => taskIds.value?.length,
        (length) => {
          if (length) emit("update:length", length);
        }
      )
    );
    const getId = (frequency: Rule): string =>
      `${frequency.every}${frequency.period}`;
    const getTitle = (frequency: Rule): string => getRecurrenceText(frequency);
    const {
      isEditing,
      quitEditing,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts,
      taskListEl,
      unselectTask,
      release: releaseTaskList,
    } = useTaskList(
      taskIds,
      {
        controller,
        console,
        contextMenu,
        isModalShown,
        isInputFocused,
      },
      { containerEl }
    );
    releaser.defer(releaseTaskList);
    return {
      containerEl,
      getId,
      getTitle,
      isEditing,
      openUrl,
      quitEditing,
      recurrentTasks,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts,
      taskIds,
      taskListEl,
      unselectTask,
    };
  },
});
</script>
<style scoped lang="scss">
.recurrences-task-list {
  height: 100%;

  .add-recurring-task {
    margin-bottom: 16px;
  }

  .items {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    flex: 1;
    .section {
      display: flex;
      flex-direction: column;
      row-gap: 8px;
      .title {
        margin-inline-start: 16px;
        @extend %font-task-regular;
      }
      .base-skeleton-list {
        margin-inline-start: 12px;
      }
    }
    .unselect-area {
      display: flex;
      flex: 1;
    }
  }
}
</style>
